import { useEffect, useState } from 'react';
import { Grid, InputLabel, OutlinedInput } from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { FormDrawer, FormDrawerGroup } from '../../../../../components/form-drawer';
import { DateField, FieldError } from '../../../../../components/form-page-parts';
import BrazilianNumberInput from '../../../../../components/brazilian-number-input';
import { ICommitmentResponse } from '../../../../../model/sge/commitment/response/commitment-response';
import { ILiquidationAddEditRequest } from '../../../../../model/sge/liquidation/request/liquidation-add-edit-request';
import { ILiquidationResponse } from '../../../../../model/sge/liquidation/response/liquidation-response';
import { hasErrors } from '../../../../../utils/validation';
import { LiquidationForm, projectTotal, toLiquidationForm, validateLiquidation } from './amendment-detail-form-utils';
import { ProgressStat, ProgressSummary } from './progress-stat';

interface LiquidationDrawerProps {
    open: boolean;
    initial?: ILiquidationResponse;
    commitment?: ICommitmentResponse;
    liquidations: ILiquidationResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: ILiquidationAddEditRequest) => void;
}

export function LiquidationDrawer({ open, initial, commitment, liquidations, isSaving, onClose, onSave }: LiquidationDrawerProps) {
    const [form, setForm] = useState<LiquidationForm>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const validation = validateLiquidation(form);
    const errors = showValidationErrors ? validation : {};
    const committedAmount = commitment?.committedAmount || 0;
    const projectedLiquidated = projectTotal(liquidations, (l) => l.liquidatedAmount, initial?.id, form.liquidatedAmount);
    const exceedsCommitment = !!commitment && projectedLiquidated > committedAmount;

    useEffect(() => {
        if (!open) return;
        setForm(toLiquidationForm(initial));
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<LiquidationForm>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave(form as ILiquidationAddEditRequest);
    }

    return (
        <FormDrawer
            open={open}
            icon={<FactCheckIcon />}
            title={isEdit ? 'Editar liquidação' : 'Nova liquidação'}
            subtitle={commitment?.commitmentNumber ? `Empenho Nº ${commitment.commitmentNumber}` : undefined}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar liquidação'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            {commitment && (
                <ProgressSummary>
                    <ProgressStat label="Liquidado do empenho" current={projectedLiquidated} total={committedAmount} color="warning" />
                </ProgressSummary>
            )}

            <FormDrawerGroup title="Liquidação">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Nº da liquidação</InputLabel>
                        <OutlinedInput fullWidth autoFocus autoComplete="off" value={form.liquidationNumber || ''} onChange={(e) => update({ liquidationNumber: e.target.value })} error={!!errors.liquidationNumber} />
                        <FieldError message={errors.liquidationNumber} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <DateField label="Data" required value={form.liquidationDate} onChange={(value) => update({ liquidationDate: value || undefined })} error={errors.liquidationDate} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Valor liquidado</InputLabel>
                        <BrazilianNumberInput
                            fullWidth
                            value={form.liquidatedAmount}
                            onChange={(liquidatedAmount) => update({ liquidatedAmount })}
                            decimals={2}
                            prefix="R$"
                            placeholder="0,00"
                            error={!!errors.liquidatedAmount}
                        />
                        <FieldError message={errors.liquidatedAmount || (exceedsCommitment && 'O valor liquidado excede o valor do empenho')} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Observações</InputLabel>
                        <OutlinedInput fullWidth multiline rows={3} value={form.observations || ''} onChange={(e) => update({ observations: e.target.value })} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
